import type { Message } from '@/shared/api';

import { MessageBorderStyleType } from '../../model';

type GenericMessage = {
  id: Message['id'];
  createdAt: Date;
  senderId: Message['senderId'];
  borderStyle: MessageBorderStyleType;
};

const MessagesEnrichBorderStylesController = <T extends GenericMessage>() => {
  const splitMessages = (data: T[]): T[][] => {
    const splittedData: T[][] = [];

    let currentSenderId: T['senderId'] | null = null;
    let currentDataToSplit: T[] = [];

    for (const message of data) {
      if (currentSenderId === null) {
        currentSenderId = message.senderId;

        currentDataToSplit.push(message);
      } else if (currentSenderId !== message.senderId) {
        if (currentDataToSplit.length !== 0) {
          splittedData.push(currentDataToSplit);
        }

        currentSenderId = message.senderId;
        currentDataToSplit = [message];
      } else {
        currentDataToSplit.push(message);
      }
    }

    if (currentDataToSplit.length !== 0) {
      splittedData.push(currentDataToSplit);
    }

    return splittedData;
  };

  const enrichBorderStyles = (splittedData: T[][]) => {
    for (const messages of splittedData) {
      if (messages.length === 1) {
        messages[0]!.borderStyle = MessageBorderStyleType.START;
        continue;
      }

      if (messages.length > 1) {
        let index: number = 0;

        for (const message of messages) {
          if (index === 0) {
            message.borderStyle = MessageBorderStyleType.START;
          } else if (index === messages.length - 1) {
            message.borderStyle = MessageBorderStyleType.END;
          } else {
            message.borderStyle = MessageBorderStyleType.CENTER;
          }

          index += 1;
        }
      }
    }
  };

  const unsplitMessages = (splittedData: T[][]): T[] => splittedData.flatMap((el) => el);

  const prepareDataToCompare = (data: T[]): string => data.map((el) => el.id).join(';');
  const checkDataFully = (data: T[], unsplittedData: T[]): boolean =>
    prepareDataToCompare(data) === prepareDataToCompare(unsplittedData);

  const execute = (data: T[]): T[] => {
    const splittedData = splitMessages(data);

    if (!checkDataFully(data, unsplitMessages(splittedData))) {
      // в таком случае будет значить, что есть ошибка в расчетах и лучше вернуть искомый список
      return data;
    }

    enrichBorderStyles(splittedData);
    return unsplitMessages(splittedData);
  };

  return { execute };
};

const MessagesFormatters = {
  sort: <T extends GenericMessage>(data: T[]): T[] =>
    [...data].sort((b, a) => b.createdAt.getTime() - a.createdAt.getTime()),

  enrichBorderStyles: <T extends GenericMessage>(data: T[]): T[] =>
    MessagesEnrichBorderStylesController<T>().execute(data),
} as const;

export { MessagesFormatters };
